/**
 * Errores de la web publica.
 *
 * Regla de ADR-034: **la interfaz decide por codigo, nunca comparando textos**. El
 * servidor manda `details.codigo` estable y un `message` humano; aca se ignora el
 * `message` del servidor y se resuelve el texto a partir del codigo, para poder
 * reescribirlo sin tocar el backend.
 *
 * Ademas se clasifica cada fallo en los tres ejes que la interfaz necesita:
 *
 *   - `incierto`  — el servidor pudo haber ejecutado la operacion. Hay que **reintentar
 *                   con la misma clave de idempotencia** (ADR-024), nunca con una nueva.
 *   - `conflicto` — el horario se ocupo mientras la persona completaba el formulario.
 *   - `campo`     — nombre del campo a marcar en el formulario. **Nunca su valor**
 *                   (docs/SEGURIDAD.md §6).
 */

import { esCodigoErrorDominio, type CodigoErrorDominio } from '@padel/domain';

/** Codigos propios del borde, que no vienen del dominio (docs/API.md §0). */
export const CODIGOS_BORDE = [
  'BOOKING_CONFLICT',
  'ENTRADA_INVALIDA',
  'APP_CHECK_REQUERIDO',
  'AUTENTICACION_REQUERIDA',
  'CREDENCIALES_INVALIDAS',
  'CONSENTIMIENTO_REQUERIDO',
  'CANCHA_NO_DISPONIBLE',
  'CONFIGURACION_AUSENTE',
  'RANGO_INVALIDO',
  'ULTIMO_PROPIETARIO_PROTEGIDO',
  'USUARIO_NO_ENCONTRADO',
  'USUARIO_DUPLICADO',
  'RESERVA_NO_ENCONTRADA',
  'CLIENTE_NO_ENCONTRADO',
  'PAGO_NO_ENCONTRADO',
  'RESERVA_YA_PAGADA',
  'COBRO_DESACTUALIZADO',
  'TRANSFERENCIA_NO_CONFIRMADA',
  'MEDIO_PAGO_NO_REGISTRADO',
  'MOVIMIENTO_NO_ENCONTRADO',
  /** Series recurrentes y bloqueos de cancha (Fase 10). */
  'SERIE_NO_ENCONTRADA',
  'OCURRENCIA_NO_ENCONTRADA',
  'OCURRENCIA_NO_REINTENTABLE',
  'BLOQUEO_NO_ENCONTRADO',
  'BLOQUEO_CON_RESERVAS',
  'BLOQUEO_INICIADO',
  'BLOQUEO_RETIRADO',
  /** Productos, stock y ventas (Fase 11B). */
  'VENTA_NO_ENCONTRADA',
  'COTIZACION_NO_DISPONIBLE',
  /** Notificaciones de WhatsApp (Fase 12). */
  'MENSAJE_NO_ENCONTRADO',
  'MENSAJE_YA_ENVIADO',
  'ERROR_INTERNO',
] as const;

/** Codigos que genera la propia web, sin que la peticion llegue a completarse. */
export const CODIGOS_CLIENTE = [
  /** No hubo respuesta: red caida, servidor apagado, CORS. Resultado desconocido. */
  'RED_NO_DISPONIBLE',
  /** La respuesta llego pero no cumple el contrato de docs/API.md. */
  'RESPUESTA_INVALIDA',
] as const;

/** Codigo de error estable que la interfaz sabe interpretar. */
export type CodigoPublico =
  CodigoErrorDominio | (typeof CODIGOS_BORDE)[number] | (typeof CODIGOS_CLIENTE)[number];

/**
 * Codigos cuyo resultado en el servidor es **desconocido**.
 *
 * Un reintento de estos tiene que llevar la **misma** clave de idempotencia: puede ser
 * que la reserva ya se haya creado y que lo unico perdido sea la respuesta.
 */
const INCIERTOS: readonly CodigoPublico[] = [
  'RED_NO_DISPONIBLE',
  'RESPUESTA_INVALIDA',
  'ERROR_INTERNO',
];

/** Codigos de conflicto de disponibilidad. Los dos significan lo mismo. */
const CONFLICTOS: readonly CodigoPublico[] = ['BOOKING_CONFLICT', 'SLOT_OCUPADO'];

/**
 * Textos en espanol de Uruguay.
 *
 * Deliberadamente poco especificos donde el detalle seria un oraculo: no se distingue
 * "esa reserva existe" de "ese dato es invalido" mas alla de lo que hace falta para
 * corregir el formulario (docs/SEGURIDAD.md §4).
 */
const MENSAJES: Partial<Record<CodigoPublico, string>> = {
  BOOKING_CONFLICT: 'Alguien tomo ese horario mientras completabas los datos. Elegi otro.',
  SLOT_OCUPADO: 'Alguien tomo ese horario mientras completabas los datos. Elegi otro.',
  FUERA_DE_VENTANA: 'Solo se puede reservar dentro de los proximos 7 dias.',
  ANTICIPACION_INSUFICIENTE: 'La reserva tiene que hacerse con al menos 5 minutos de anticipacion.',
  TARIFA_INEXISTENTE: 'Esa duracion no se ofrece en el horario elegido.',
  DURACION_INVALIDA: 'La duracion tiene que ser de 60 o 90 minutos.',
  INICIO_NO_ALINEADO: 'El horario tiene que empezar en punto o y media.',
  CANCHA_INVALIDA: 'La cancha indicada no existe.',
  CANCHA_NO_DISPONIBLE: 'Esta cancha no está disponible para nuevas reservas.',
  CEDULA_INVALIDA: 'La cedula no es valida. Revisa el numero.',
  TELEFONO_INVALIDO: 'El telefono no es valido. Revisa el numero.',
  CUOTA_EXCEDIDA: 'Hubo demasiados intentos seguidos. Espera un momento y volve a probar.',
  CONSENTIMIENTO_REQUERIDO: 'Falta aceptar las condiciones de cancelacion.',
  APP_CHECK_REQUERIDO: 'No se pudo verificar la aplicacion. Recarga la pagina.',
  AUTENTICACION_REQUERIDA: 'No se pudo iniciar la sesion. Recarga la pagina.',
  CREDENCIALES_INVALIDAS: 'El usuario o la contrasena no son correctos.',
  ENTRADA_INVALIDA: 'Los datos enviados no son validos. Revisa el formulario.',
  RANGO_INVALIDO: 'El rango de fechas pedido no es valido.',
  ULTIMO_PROPIETARIO_PROTEGIDO: 'Tiene que quedar al menos una persona propietaria activa.',
  USUARIO_NO_ENCONTRADO: 'La cuenta interna indicada no existe.',
  USUARIO_DUPLICADO: 'Ese nombre de usuario ya esta en uso.',
  RESERVA_NO_ENCONTRADA: 'Esa reserva no existe o fue dada de baja.',
  MOTIVO_REQUERIDO: 'Escribi un motivo para poder registrar esta accion.',
  LIMITE_AJUSTE_EXCEDIDO:
    'El monto supera el tope de ajuste de tu rol. Pedíselo a la persona propietaria.',
  PAGO_YA_REVERTIDO: 'Ese pago ya fue revertido. No se puede revertir dos veces.',
  SALDO_DIVERGENTE: 'El saldo no coincide con el libro mayor. Avisa a la persona propietaria.',
  PAGO_VACIO: 'El pago tiene que tener al menos un medio con importe.',
  PAGO_INCONSISTENTE: 'Los importes del pago no cierran. Revisa las lineas.',
  CLIENTE_NO_ENCONTRADO: 'No hay ninguna ficha con esos datos.',
  PAGO_NO_ENCONTRADO: 'Ese pago no existe.',
  RESERVA_YA_PAGADA: 'Esta reserva ya tiene el pago registrado. No se puede cobrar dos veces.',
  COBRO_DESACTUALIZADO:
    'El cobro de la reserva cambió. Revisá el método y el importe antes de continuar.',
  TRANSFERENCIA_NO_CONFIRMADA: 'Confirma que la transferencia fue recibida antes de registrarla.',
  MEDIO_PAGO_NO_REGISTRADO:
    'Esta reserva no tiene un metodo de pago definido. Corregila antes de cobrar.',
  CAJA_CERRADA:
    'No hay ninguna caja abierta. Abri la caja para poder cobrar en efectivo o registrar este movimiento.',
  CAJA_YA_ABIERTA: 'Ya hay una caja abierta. Cerrala antes de abrir otra.',
  CAJA_LLENA: 'Esta caja llego al tope de movimientos. Cerrala y abri una nueva.',
  SESION_CERRADA: 'Esa sesion de caja esta cerrada y no admite cambios.',
  LIMITE_CORRECCION_EXCEDIDO:
    'El monto supera el tope de correccion de tu rol. Pediselo a la persona propietaria.',
  MOVIMIENTO_YA_CORREGIDO: 'Ese movimiento ya fue corregido. No se corrige dos veces.',
  MOVIMIENTO_NO_ENCONTRADO: 'Ese movimiento de caja no existe.',
  ARQUEO_DIVERGENTE:
    'El efectivo esperado no coincide con la suma de los movimientos. Avisa a la persona propietaria antes de cerrar.',
  CLIENTE_BLOQUEADO: 'Esa cuenta esta bloqueada para reservas nuevas.',
  TRANSICION_INVALIDA: 'Esa reserva ya cambio de estado. Actualiza la agenda y volve a mirar.',
  CONFIGURACION_AUSENTE: 'El sistema no esta configurado. Avisa al complejo.',
  HORA_LOCAL_INEXISTENTE: 'Ese horario no existe por un cambio de hora. Elegi otro.',
  PERMISO_DENEGADO: 'No tenes permiso para hacer esta operacion.',

  // ── Series recurrentes y bloqueos (Fase 10) ──────────────────────────────
  RANGO_INVERTIDO: 'La fecha final es anterior a la inicial. Revisa el rango.',
  MAXIMO_OCURRENCIAS_EXCEDIDO:
    'La serie tendria mas de 104 turnos. Acorta el rango o carga dos series.',
  TEMPORADA_SIN_FECHAS: 'Elegi al menos una fecha para la temporada.',
  SERIE_SIN_OCURRENCIAS: 'Con esos datos la serie no genera ningun turno.',
  SERIE_NO_ENCONTRADA: 'Esa serie no existe.',
  OCURRENCIA_NO_ENCONTRADA: 'No hay ningun turno de la serie desde esa fecha.',
  OCURRENCIA_NO_REINTENTABLE: 'Ese turno ya se creo: reintentarlo lo duplicaria.',
  RANGO_VACIO: 'El bloqueo no cubre ningun horario. Revisa el comienzo y el final.',
  MAXIMO_SLOTS_EXCEDIDO: 'Un bloqueo no puede durar mas de 48 horas. Carga varios.',
  BLOQUEO_NO_ENCONTRADO: 'Ese bloqueo no existe.',
  BLOQUEO_CON_RESERVAS:
    'Hay reservas en ese horario. Resolve cada una y volve a intentar el bloqueo.',
  BLOQUEO_INICIADO:
    'Ese bloqueo ya empezo y no se puede modificar ni retirar. Podes dejar una anotacion.',
  BLOQUEO_RETIRADO: 'Ese bloqueo ya fue retirado.',

  // ── Productos, stock y ventas (Fase 11B) ─────────────────────────────────
  //
  // Sin estos textos, un codigo perfectamente conocido —`STOCK_INSUFICIENTE`— caeria en
  // el mensaje por defecto y el mostrador leeria "ocurrio un error inesperado" ante algo
  // que no tiene nada de inesperado.
  STOCK_INSUFICIENTE: 'No hay stock suficiente. Actualiza el catalogo y volve a mirar.',
  PRODUCTO_INACTIVO: 'Ese producto esta desactivado y no se puede vender.',
  PRODUCTO_INEXISTENTE: 'Ese producto no existe.',
  SKU_DUPLICADO: 'Ya hay un producto con ese codigo interno.',
  SKU_INVALIDO: 'El codigo interno solo admite letras, numeros y guiones.',
  CANTIDAD_INVALIDA: 'Las cantidades tienen que ser numeros enteros mayores que cero.',
  MONEDA_INVALIDA: 'La moneda elegida no es valida para esta operacion.',
  CLIENTE_REQUERIDO: 'Para cargar a cuenta corriente hace falta elegir un cliente.',
  VENTA_VACIA: 'Carga al menos un producto antes de cobrar.',
  VENTA_INCONSISTENTE: 'Los importes no cierran contra el total. Revisa los medios de pago.',
  VENTA_NO_ANULABLE:
    'Esa venta no se puede anular: ya esta anulada o tiene devoluciones registradas.',
  VENTA_NO_DEVOLVIBLE: 'Esa venta no admite devoluciones: esta anulada.',
  VENTA_NO_ENCONTRADA: 'Esa venta no existe.',
  DEVOLUCION_EXCEDE_VENTA: 'No se puede devolver mas de lo que esa venta tiene.',
  CATEGORIA_INEXISTENTE: 'Esa categoria no existe o esta desactivada.',
  MAXIMO_LINEAS_EXCEDIDO: 'Son demasiadas lineas para una sola operacion. Carga dos.',
  INVENTARIO_ABIERTO: 'Ya hay un conteo de inventario abierto.',
  COTIZACION_NO_DISPONIBLE:
    'No pudimos obtener la cotizacion oficial del BROU. Volve a intentar antes de confirmar.',

  // ── Notificaciones (Fase 12) ─────────────────────────────────────────────
  MENSAJE_NO_ENCONTRADO: 'Ese mensaje no existe en la cola de notificaciones.',
  MENSAJE_YA_ENVIADO: 'Ese mensaje ya se envio: reintentarlo lo mandaria dos veces.',

  // ── Paquetes prepagos (ADR-080, ADR-081) ─────────────────────────────────
  //
  // En la interfaz se habla de «partidos», nunca de «créditos»: crédito ya es la tarjeta
  // de crédito y, en reportes, el saldo a favor.
  OFERTA_PAQUETE_NO_ENCONTRADA: 'Esa oferta de paquete no existe. Actualizá el catálogo.',
  OFERTA_PAQUETE_INACTIVA:
    'Esa oferta de paquete no está activa y no se puede vender. Elegí otra o pedí que la activen.',
  OFERTA_PAQUETE_DESACTUALIZADA:
    'La oferta cambió desde que la abriste. Actualizá el catálogo y revisá las condiciones antes de continuar.',
  OFERTA_PAQUETE_INCOMPLETA:
    'Para activar la oferta falta elegir al menos una cancha y, si tiene horario restringido, al menos una franja con días.',
  PAQUETE_NO_ENCONTRADO: 'Ese paquete no existe. Actualizá los paquetes del cliente.',
  PAQUETE_DE_OTRO_TITULAR:
    'Ese paquete es de otro cliente. Solo puede usarlo la persona titular del paquete.',
  PAQUETE_NO_APLICA:
    'El turno no cumple las condiciones del paquete. Revisá la cancha, la duración, el día, el horario y que el turno termine antes del vencimiento.',
  PAQUETE_SIN_PARTIDOS:
    'El paquete no tiene partidos disponibles para ese turno. Puede que alguien del mostrador acabe de usar el último.',
  RESERVA_CUBIERTA_POR_PAQUETE:
    'Esta reserva está cubierta por un partido de paquete: no se cobra ni se cambia su forma de pago.',
  RESERVA_NO_ADMITE_PAQUETE:
    'Esta reserva no admite paquete: tiene bonificación o descuento, es parte de una serie o ya está cubierta.',
  TRASLADO_PAQUETE_REQUIERE_DECISION:
    'El nuevo horario no puede usar el mismo partido. Elegí otro paquete o dejá el turno como reserva a cobrar.',
  POLITICA_PAQUETE_CAMBIO:
    'Lo que pasa con el partido cambió desde la vista previa (por ejemplo, venció el plazo de anticipación). Revisalo y confirmá de nuevo.',
  PAGO_DE_PAQUETE:
    'Ese pago es la venta de un paquete y no se revierte. Para devolver dinero usá el reintegro del paquete.',
  REINTEGRO_EXCEDE_DISPONIBLES:
    'No hay tantos partidos para reintegrar. Los partidos reservados, vencidos o de cortesía no se reintegran: resolvé primero las reservas futuras del paquete.',
  REINTEGRO_DESACTUALIZADO:
    'El monto a reintegrar cambió desde que lo calculaste. Actualizá el paquete y confirmá de nuevo.',
  EXTENSION_INVALIDA:
    'La nueva fecha no alarga el vencimiento de ningún partido. Elegí una fecha posterior al vencimiento actual.',
  AJUSTE_PAQUETE_INVALIDO:
    'El ajuste no es válido. Revisá la cantidad, el tope de partidos del paquete y que haya partidos disponibles.',

  RED_NO_DISPONIBLE: 'No pudimos comunicarnos con el servidor. Revisa tu conexion a internet.',
  RESPUESTA_INVALIDA: 'El servidor respondio algo inesperado. Volve a intentar.',
  ERROR_INTERNO: 'Ocurrio un error inesperado. Volve a intentar en unos minutos.',
};

const MENSAJE_POR_DEFECTO = 'Ocurrio un error inesperado. Volve a intentar en unos minutos.';

/** Texto para una persona a partir de un codigo estable. */
export function mensajeDe(codigo: CodigoPublico): string {
  return MENSAJES[codigo] ?? MENSAJE_POR_DEFECTO;
}

/** `true` si el texto es un codigo que esta interfaz reconoce. */
export function esCodigoPublico(valor: string): valor is CodigoPublico {
  return (
    esCodigoErrorDominio(valor) ||
    (CODIGOS_BORDE as readonly string[]).includes(valor) ||
    (CODIGOS_CLIENTE as readonly string[]).includes(valor)
  );
}

/** Un fallo ya clasificado, listo para que la interfaz decida que hacer. */
export class ErrorPublico extends Error {
  readonly codigo: CodigoPublico;
  readonly campo: string | undefined;
  /** El servidor pudo haber ejecutado la operacion: reintentar con la MISMA clave. */
  readonly incierto: boolean;
  /** El horario se ocupo: hay que refrescar la disponibilidad. */
  readonly conflicto: boolean;

  constructor(codigo: CodigoPublico, campo?: string) {
    super(mensajeDe(codigo));
    this.name = 'ErrorPublico';
    this.codigo = codigo;
    this.campo = campo;
    this.incierto = INCIERTOS.includes(codigo);
    this.conflicto = CONFLICTOS.includes(codigo);
  }
}

/** Lee `details.codigo` y `details.campo` de un error de callable, sin castear nada. */
function leerDetalles(causa: unknown): { codigo?: string; campo?: string } {
  if (typeof causa !== 'object' || causa === null || !('details' in causa)) {
    return {};
  }

  const detalles: unknown = Reflect.get(causa, 'details');
  if (typeof detalles !== 'object' || detalles === null) {
    return {};
  }

  const codigo: unknown = Reflect.get(detalles, 'codigo');
  const campo: unknown = Reflect.get(detalles, 'campo');

  return {
    ...(typeof codigo === 'string' ? { codigo } : {}),
    ...(typeof campo === 'string' ? { campo } : {}),
  };
}

/** Lee el `code` de un `FunctionsError` (`"functions/unavailable"` -> `"unavailable"`). */
function leerCodigoTransporte(causa: unknown): string | undefined {
  if (typeof causa !== 'object' || causa === null || !('code' in causa)) {
    return undefined;
  }
  const codigo: unknown = Reflect.get(causa, 'code');
  if (typeof codigo !== 'string') {
    return undefined;
  }
  return codigo.startsWith('functions/') ? codigo.slice('functions/'.length) : codigo;
}

/** Codigos de transporte que significan "no se pudo llegar al servidor". */
const TRANSPORTE_SIN_RESPUESTA: readonly string[] = [
  'unavailable',
  'deadline-exceeded',
  'cancelled',
];

/**
 * Convierte cualquier cosa lanzada en un `ErrorPublico` clasificado.
 *
 * El orden importa: primero el codigo de negocio de `details`, que es el estable; solo
 * si no viene se cae al codigo de transporte, que es el que distingue "no hubo red" de
 * "el servidor contesto que no".
 */
export function traducirError(causa: unknown): ErrorPublico {
  if (causa instanceof ErrorPublico) {
    return causa;
  }

  const { codigo, campo } = leerDetalles(causa);
  if (codigo !== undefined && esCodigoPublico(codigo)) {
    return new ErrorPublico(codigo, campo);
  }

  const transporte = leerCodigoTransporte(causa);
  if (transporte !== undefined) {
    if (TRANSPORTE_SIN_RESPUESTA.includes(transporte)) {
      return new ErrorPublico('RED_NO_DISPONIBLE');
    }
    if (transporte === 'permission-denied') {
      return new ErrorPublico('PERMISO_DENEGADO');
    }
    if (transporte === 'unauthenticated') {
      return new ErrorPublico('AUTENTICACION_REQUERIDA');
    }
    return new ErrorPublico('ERROR_INTERNO');
  }

  // Sin `code` ni `details`: un TypeError de `fetch`, un timeout del navegador o el
  // dispositivo sin conexion. El resultado en el servidor es desconocido.
  return new ErrorPublico('RED_NO_DISPONIBLE');
}
